/* Fronta volání — JEDINÝ zdroj pravdy o tom, koho je potřeba obvolat.
 *
 * PROČ TO EXISTUJE (majitel, 8/2026):
 * „Zrcadlo volání musí být někde na nástěnce, ne? Jako nejdůležitější."
 *
 * Fronta se od té chvíle počítá na DVOU obrazovkách (stránka /crm/calls
 * a panel na nástěnce). Kdyby si každá počítala po svém, poradce uvidí
 * dvě různá čísla a přestane věřit oběma. Filtr proto žije tady a obě
 * místa ho jen volají.
 *
 * Modul je ČISTÝ — bez Reactu a bez API — aby šel testovat samostatně
 * a aby ho šlo použít i mimo obrazovku (např. v notifikacích).
 */

/* Pole za všech okolností. API u nového uživatele / při chybě umí vrátit
 * objekt, null i HTML string; ne-objektové položky (pole [null, {...}])
 * shazovaly nástěnku na `null.stage`. */
const asArr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);
const txt = (v) => (typeof v === "string" ? v : "");

/**
 * Použitelné kontakty ze surové odpovědi API. Sdílené, aby se na jedné
 * obrazovce nestalo, že jeden seznam rozbitá data přežije a druhý na nich spadne
 * (a s ním celá stránka).
 */
export const seznamKontaktu = asArr;

/** Fáze, ve kterých kontakt do fronty volání patří. */
export const FAZE_VE_FRONTE = ["lead", "calling"];

/** Stavy hovoru, které kontakt z fronty vyřadí (má schůzku / nemá zájem). */
export const UZAVRENE_STAVY = ["meeting", "not_interested"];

/**
 * Patří kontakt do fronty volání?
 *
 * Přesně to, co dělala stránka /crm/calls v `lists.to_call`:
 *   stage ∈ {lead, calling}  A ZÁROVEŇ  call_status ∉ {meeting, not_interested}
 */
export function jeVeFronte(c) {
  if (!c || typeof c !== "object") return false;
  return FAZE_VE_FRONTE.includes(txt(c.stage)) && !UZAVRENE_STAVY.includes(txt(c.call_status));
}

/** Fronta volání ze surového seznamu kontaktů (pořadí vstupu zachováno). */
export function frontaVolani(contacts) {
  return asArr(contacts).filter(jeVeFronte);
}

/**
 * Kdy kontakt do fronty přišel (ms), nebo null když to z dat nejde poznat.
 * Neznámé datum SE NEDOMÝŠLÍ — takový kontakt jen nemá kde v řazení tvrdit,
 * že čeká dlouho.
 */
export function cekaOd(c) {
  const raw = c?.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  const ms = d.getTime();
  return isNaN(ms) ? null : ms;
}

/** Jméno kontaktu do seznamu. Bez jména zkusí firmu, jinak prázdno (ne výmysl). */
export function jmenoKontaktu(c) {
  const jm = [c?.name, c?.surname].map(txt).filter(Boolean).join(" ").trim();
  return jm || txt(c?.company).trim();
}

/**
 * Fronta seřazená od NEJDÉLE ČEKAJÍCÍHO. Kdo čeká nejdéle, vypadne první —
 * jinak nejstarší leady tiše vyhnijí na konci seznamu.
 *
 * Kontakty bez použitelného data přijdou nakonec (netvrdíme, že čekají dlouho),
 * pořadí mezi nimi je stabilní podle jména a id, ať se seznam pod rukama nemíchá.
 */
export function serazenaFronta(contacts) {
  return frontaVolani(contacts)
    .map((c, poradi) => ({ c, poradi, od: cekaOd(c) }))
    .sort((a, b) => {
      if (a.od != null && b.od != null && a.od !== b.od) return a.od - b.od;
      if (a.od != null && b.od == null) return -1;
      if (a.od == null && b.od != null) return 1;
      const jm = jmenoKontaktu(a.c).localeCompare(jmenoKontaktu(b.c), "cs");
      if (jm !== 0) return jm;
      const id = txt(a.c.id).localeCompare(txt(b.c.id));
      return id !== 0 ? id : a.poradi - b.poradi;
    })
    .map((x) => x.c);
}

/** Dnešní datum ISO v LOKÁLNÍM čase — toISOString() by mezi půlnocí a ~2:00 vracel včerejšek. */
export function dnesISOLokalne(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Co se má u kontaktu změnit po výsledku hovoru.
 *
 * Sdílené se stránkou /crm/calls, aby „Domluveno" na nástěnce posunulo fázi
 * úplně stejně jako tlačítko Schůzka ve Zrcadle. Vrací jen patch — uložení
 * (API, optimistický update, rollback) si řeší volající.
 */
export function zmenaStavuVolani(contact, status, dnes = dnesISOLokalne()) {
  const next = { call_status: status, last_call: dnes };
  /* Fázi jen zvedat, nikdy nesnížit (18. 10. 2026): v „Nových leadech" je i
   * klient v Podpisu, který projevil nový zájem — „Schůzka" ho nesmí stáhnout
   * na a1 a „Nezájem" o nový produkt z něj neudělá ztraceného klienta. */
  const rana = ["", "lead", "calling", "lost"].includes(contact?.stage || "");
  if (status === "meeting" && rana) next.stage = "a1";
  if (status === "not_interested" && ["", "lead", "calling"].includes(contact?.stage || "")) next.stage = "lost";
  if (status === "not_reached" || status === "callback" || status === "call_later") {
    if (contact?.stage === "lead") next.stage = "calling";
  }
  return next;
}

/**
 * Kam po „Schůzka“ ve Zrcadle volání (nález V2, 19. 10. 2026): rovnou zápis
 * termínu do diáře (schůzka navázaná na kontakt) a po uložení zpět k volání.
 * Bez události v diáři se domluvená schůzka nepočítala do 7. dne adaptace
 * ani do Start-Kitu — nováček dělal přesně to, co ho akademie učí, a nikde
 * se mu to neukázalo.
 */
export function odkazSchuzkyDoDiare(contact) {
  const jmeno = [contact?.name, contact?.surname].filter(Boolean).join(" ").trim();
  const p = new URLSearchParams({ newActivity: "1", type: "schuzka", contact_id: String(contact?.id ?? "") });
  p.set("title", jmeno ? `Schůzka — ${jmeno}` : "Schůzka");
  p.set("potom", "/crm/calls");
  return `/diar?${p.toString()}`;
}

/**
 * Kolik kontaktů má domluvenou schůzku — JEDNO měřítko pro Start-Kit
 * (stejné jako 7. den adaptace: schůzka v diáři navázaná na kontakt),
 * plus starší cesty (a1_date, už podepsaní klienti). Každý kontakt jednou.
 */
export function pocetKontaktuSeSchuzkou(contacts, tasks) {
  const ids = new Set();
  for (const t of Array.isArray(tasks) ? tasks : []) {
    if (t && t.activity_type === "schuzka" && t.contact_id) ids.add(String(t.contact_id));
  }
  for (const c of Array.isArray(contacts) ? contacts : []) {
    if (c && c.id != null && (c.a1_date || ["signed", "servis"].includes(c.stage))) ids.add(String(c.id));
  }
  return ids.size;
}
